import type { Level } from '#@/levels.ts'
import type { LogRecord } from '#@/record.ts'

export type LogFormat = (typeof LOG_FORMATS)[number]

export const LOG_FORMATS = ['json', 'pretty'] as const

export function isLogFormat(value: unknown): value is LogFormat {
  return LOG_FORMATS.some((format) => format === value)
}

/** What each variant under `runtime/` exports. The package selects one variant by export condition (AD5). */
export type Runtime = {
  /** The level and format when the environment sets neither. */
  defaults: () => { level: Level; format: LogFormat }
  /** `LOG_LEVEL` and `LOG_FORMAT` as the environment gives them; `undefined` when unset or unreadable (R15). */
  readConfig: () => { level: string | undefined; format: string | undefined }
  emit: (entry: LogEntry) => void
}

export type LogEntry = { level: Level; record: LogRecord; prefixes: readonly string[]; format: LogFormat }

/**
 * A guarded `process.env` read (R15). `process` is absent in a browser, a getter-only shim in some bundles, and an
 * object without `env` in others; every shape returns `undefined` instead of throwing. The read goes through
 * `globalThis` so the type-checker does not assume Node's `process`.
 */
export function readProcessEnv(name: string): string | undefined {
  try {
    const candidate: unknown = Reflect.get(globalThis, 'process')
    if (typeof candidate !== 'object' || candidate === null || !('env' in candidate)) {
      return undefined
    }
    const { env } = candidate
    if (typeof env !== 'object' || env === null) {
      return undefined
    }
    const value: unknown = Reflect.get(env, name)
    return typeof value === 'string' ? value : undefined
  } catch {
    return undefined
  }
}
